// src/oura/ask/facts.ts
//
// Ask Oura, job 2 widened: the facts Oura already shows on another screen,
// answered in one or two sentences with a link to that screen. Score and site
// health, scan dates, which questions, Google search, site issues, the plan,
// the people, and setup. Each reads the same records the screen reads
// (liveFacts.ts), so the chat and the screen never disagree. Nothing here
// writes. A fact Oura does not have is said so in plain words, never a zero.

import { dayMonth } from "../visibility/format";
import type { ClientData } from "./useAskData";
import { andList, contactOf, hrefs, n, p, s, src, visSource } from "./text";
import type { AskAnswer, AskBlock, AskRow, AskTableRow } from "./types";

type Fact = "score" | "dates" | "questions" | "search" | "issues" | "plan" | "people" | "setup";

/** The screen a fact lives on, by name: the citation under each sentence. */
const SETUP_WORDS = /\b(setup|set up|onboarding|access|connected|connect|disconnected|search console connected|analytics connected|gbp connected)\b/;
const SEARCH_WORDS = /\b(google|search console|clicks?|impressions?|avg\.? position|average position|rank(ing|ed)?|positions?|traffic|visits?|visitors?|sessions|landing pages?|top pages?)\b/;
const GOOGLE_AI = /\bgoogle'?s ai\b|\bai overviews?\b/;

/** Which fact a question asks for, or null when it is one of the older intents' (or nothing Oura knows). */
export function factOf(t: string): Fact | null {
    if (/\banswer plan\b/.test(t)) return null;
    // Lost and gained questions are a comparison this file answers with the list itself.
    if (/\bquestions?\b/.test(t) && /\b(lost|gained|won|lose|gain)\b/.test(t)) return "questions";
    // Any other explicit comparison ("what changed since the last scan", "is site health up or down")
    // belongs to the changed intent, never to a snapshot answer.
    if (/\b(since|changed?|changes|compared?|comparison|difference|up or down|went (up|down))\b/.test(t)) return null;
    if (SETUP_WORDS.test(t) && !/\bhow (do|can) i\b/.test(t)) return "setup";
    if (/\b(scan|check)/.test(t) && /\b(when (was|is|did|will|does)|what date|which (day|date)|next (ai |monthly )?(scan|check)|last (ai |monthly )?(scan|check|checked|scanned))\b/.test(t)) return "dates";
    if (/\b(score|site health|health score|aeo score|overall rating|rating)\b/.test(t)) return "score";
    if (/\bquestions?\b/.test(t) && /\b(which|what|list|show|how many|named|missing|lost|gained|won|target|not)\b/.test(t)) return "questions";
    if (SEARCH_WORDS.test(t) && !GOOGLE_AI.test(t)) return "search";
    if (/\b(site )?(issues?|errors?|warnings?|notices?|broken|problems?|audit|wrong with (the |their )?(site|website))\b/.test(t) && !/\bhow (do|can) i\b/.test(t)) return "issues";
    if (/\b(contract|the plan|their plan|in the plan|what plan|which plan|term|which month|what month|month are (we|they)|how many months|end date|ends?|ending|expire|expiry|renew)\b/.test(t)) return "plan";
    if (/\b(contact|account manager|who (is|'s) (the |their )?(client|owner|person|contact|am|manager)|phone|email address|whatsapp|outlets?|branch(es)?|what (do they|services)|services|category|where are they)\b/.test(t)) return "people";
    return null;
}

const notYet = (d: ClientData, what: string, screen: string, to: string): AskAnswer => ({
    blocks: [p(s(`Oura has no ${what} for ${d.name} yet.`, src(screen, to)))],
    followUps: ["How do I start a rescan?", "Catch me up"],
});

/* ------------------------------------------------------------------------- *
 * Score and site health
 * ------------------------------------------------------------------------- */

function scoreAnswer(d: ClientData): AskAnswer {
    const f = d.facts;
    const id = d.businessId;
    const overview = src("Overview", hrefs.overview(id));
    if (!f || !f.scores || (f.scores.aeo === null && f.scores.siteHealth === null)) return notYet(d, "score", "Overview", hrefs.overview(id));
    const sc = f.scores;
    const when = f.lastScanAt ? `, from the ${dayMonth(f.lastScanAt)} scan` : "";
    const parts: string[] = [];
    if (sc.aeo !== null) parts.push(`AEO score ${sc.aeo}`);
    if (sc.siteHealth !== null) parts.push(`site health ${sc.siteHealth}`);
    const sentences = [s(`${d.name}: ${andList(parts)}${when}.`, overview)];
    if (sc.prev) {
        const move = (now: number | null, before: number | null, what: string) =>
            now === null || before === null ? null : now === before ? `${what} unchanged` : `${what} ${now > before ? "up" : "down"} ${Math.abs(now - before)}`;
        const moves = [move(sc.aeo, sc.prev.aeo, "AEO"), move(sc.siteHealth, sc.prev.siteHealth, "site health")].filter((x): x is string => !!x);
        if (moves.length) sentences.push(s(`Since the ${dayMonth(sc.prev.at)} scan: ${andList(moves)}.`, src("History", hrefs.history(id))));
    } else {
        sentences.push(s("There is only one scored scan, so there is no change to show yet.", src("History", hrefs.history(id))));
    }
    if (d.vis) sentences.push(s(`AI names them in ${d.vis.named} of ${d.vis.of} questions.`, visSource(d)));
    return { blocks: [p(...sentences)], followUps: ["What changed since the last scan?", "What's broken on their site?"] };
}

/* ------------------------------------------------------------------------- *
 * Scan dates
 * ------------------------------------------------------------------------- */

function datesAnswer(d: ClientData): AskAnswer {
    const f = d.facts;
    const id = d.businessId;
    const overview = src("Overview", hrefs.overview(id));
    if (!f || !f.lastScanAt) return notYet(d, "finished scan", "Overview", hrefs.overview(id));
    const sentences = [s(`The last AI check for ${d.name} ran on ${dayMonth(f.lastScanAt)}.`, src("History", hrefs.history(id)))];
    if (f.nextScanAt) sentences.push(s(`The next monthly check is ${dayMonth(f.nextScanAt)}. It asks the same questions.`, overview));
    else if (d.delivery?.nextCheck) sentences.push(s(`The next check is ${dayMonth(d.delivery.nextCheck)}.`, src("Delivery", hrefs.delivery(id))));
    else sentences.push(s("No next check is scheduled. Monthly checks are off for this client, or the plan has not started.", overview));
    return { blocks: [p(...sentences)], followUps: ["How do I start a rescan?", "What changed since the last scan?"] };
}

/* ------------------------------------------------------------------------- *
 * Which questions
 * ------------------------------------------------------------------------- */

function questionsAnswer(t: string, d: ClientData): AskAnswer {
    const f = d.facts;
    const id = d.businessId;
    const m = f?.vis;
    if (!m || m.headline.of <= 0) return notYet(d, "AI check", "Visibility", hrefs.visibility(id));
    const want: "missing" | "named" | "lost" | "gained" = /\b(lost|dropped|used to)\b/.test(t) ? "lost" : /\b(gained|won|new(ly)?)\b/.test(t) ? "gained" : /\b(not|missing|unnamed|without|target|should)\b/.test(t) ? "missing" : "named";
    const rows = m.rows.filter((r) => r.measured);
    const missing = rows.filter((r) => !r.named);
    const named = rows.filter((r) => r.named);
    const vis = visSource(d);
    const toRow = (r: (typeof rows)[number]): AskRow => ({
        title: r.text,
        sub: r.rivals.length ? `Named instead: ${r.rivals.slice(0, 2).join(", ")}` : undefined,
        to: hrefs.visibility(id),
    });
    if (want === "lost" || want === "gained") {
        // The scan before, per question, where the history keeps it.
        const prior = f?.visInputs?.prior?.cells ?? null;
        if (!prior) return { blocks: [p(s("There is only one scan to read, so no question has been lost or gained yet.", vis))], followUps: ["Which questions are they not named in?"] };
        const was = (r: (typeof rows)[number]) => {
            const cells = prior[r.key];
            return cells ? Object.values(cells).some((c) => c?.named) : null;
        };
        const changed = rows.filter((r) => (want === "lost" ? r.named === false && was(r) === true : r.named === true && was(r) === false));
        if (!changed.length) return { blocks: [p(s(`No question was ${want} since the scan before.`, vis))], followUps: ["Which questions are they not named in?"] };
        return {
            blocks: [p(s(`${n(changed.length, "question")} ${want} since the scan before.`, vis)), { kind: "list", title: want === "lost" ? "Lost" : "Gained", tone: want === "lost" ? "amber" : "blue", rows: changed.slice(0, 8).map(toRow), more: changed.length > 8 ? `${changed.length - 8} more on Visibility` : undefined }],
            followUps: ["Who is named instead?", "What changed since the last scan?"],
        };
    }
    const list = want === "missing" ? missing : named;
    const lead = want === "missing" ? `AI does not name ${d.name} in ${n(missing.length, "question")} of ${rows.length}.` : `AI names ${d.name} in ${n(named.length, "question")} of ${rows.length}.`;
    const blocks: AskBlock[] = [p(s(lead, vis))];
    if (list.length) blocks.push({ kind: "list", title: want === "missing" ? "Not named" : "Named", tone: want === "missing" ? "amber" : "blue", rows: list.slice(0, 8).map(toRow), more: list.length > 8 ? `${list.length - 8} more on Visibility` : undefined });
    blocks.push({ kind: "go", go: { label: want === "missing" ? "Open Visibility, Missing you" : "Open Visibility", to: want === "missing" ? hrefs.missing(id) : hrefs.visibility(id) } });
    return { blocks, followUps: want === "missing" ? ["Who is named instead?", "Start the page drafts"] : ["Which questions are they not named in?"] };
}

/* ------------------------------------------------------------------------- *
 * Google search
 * ------------------------------------------------------------------------- */

function searchAnswer(d: ClientData): AskAnswer {
    const f = d.facts;
    const id = d.businessId;
    const screen = src("Search performance", `${hrefs.overview(id)}/search`);
    const sp = f?.search;
    const connectors = src("Connectors", `${hrefs.overview(id)}/connectors`);
    if (!f || !sp || !sp.connected) {
        return {
            blocks: [p(s(`Search Console is not connected for ${d.name}, so Oura has no Google clicks or positions.`, connectors))],
            followUps: ["What access are we missing?", "How is this client doing on AI?"],
        };
    }
    // Connected, nothing to count yet: the Overview tile's own words on why.
    if (sp.state && !sp.state.hasData) return { blocks: [p(s(`${sp.state.label} for ${d.name}. ${sp.state.detail}`, screen))], followUps: ["How do I start a rescan?", "How is this client doing on AI?"] };
    if (sp.clicks === null && sp.avgPosition === null) return { blocks: [p(s(`Search Console is connected for ${d.name}, but no clicks or positions have come through yet.`, screen))], followUps: ["Catch me up"] };
    const days = sp.days ? `in the last ${n(sp.days, "day")}` : "in the latest window";
    const parts: string[] = [];
    if (sp.clicks !== null) parts.push(`${n(sp.clicks, "Google click")}`);
    if (sp.impressions !== null) parts.push(`${sp.impressions.toLocaleString("en-GB")} impressions`);
    if (sp.avgPosition !== null) parts.push(`average position ${sp.avgPosition.toFixed(1)}`);
    const blocks: AskBlock[] = [p(s(`${d.name} ${days}: ${andList(parts)}.`, screen))];
    if (sp.topPages.length) {
        const rows: AskTableRow[] = sp.topPages.map((pg) => ({ label: pg.url.replace(/^https?:\/\/[^/]+/, "") || "/", value: n(pg.clicks, "click"), cite: screen }));
        blocks.push({ kind: "table", rows });
    }
    return { blocks, followUps: ["How is this client doing on AI?", "What's their score?"] };
}

/* ------------------------------------------------------------------------- *
 * Site issues
 * ------------------------------------------------------------------------- */

function issuesAnswer(d: ClientData): AskAnswer {
    const f = d.facts;
    const id = d.businessId;
    const site = src("Site", hrefs.site(id));
    if (!f || !f.issues) return notYet(d, "site crawl", "Site", hrefs.site(id));
    if (!f.issues.length) return { blocks: [p(s(`The last crawl found no issues on ${d.name}'s site.`, site))], followUps: ["What's their score?"] };
    const c = d.issues;
    const counts = c ? [c.error ? n(c.error, "error") : null, c.warning ? n(c.warning, "warning") : null, c.notice ? n(c.notice, "notice") : null].filter((x): x is string => !!x) : [];
    const lead = `The last crawl found ${andList(counts)} on ${d.name}'s site${f.lastScanAt ? `, on ${dayMonth(f.lastScanAt)}` : ""}.`;
    const rows: AskRow[] = f.issues.slice(0, 5).map((i) => ({ title: i.label, meta: `${i.severity === "error" ? "Error" : i.severity === "warning" ? "Warning" : "Notice"} · ${n(i.pages, "page")}`, late: i.severity === "error", to: hrefs.issue(id, i.key) }));
    return {
        blocks: [p(s(lead, site)), { kind: "list", title: "Worst first", tone: "amber", rows, more: f.issues.length > 5 ? `${f.issues.length - 5} more on Site` : undefined }],
        followUps: ["How do I fix a site issue?", "What's their score?"],
    };
}

/* ------------------------------------------------------------------------- *
 * The plan
 * ------------------------------------------------------------------------- */

function planAnswer(d: ClientData): AskAnswer {
    const id = d.businessId;
    const pl = d.delivery;
    const plan = src("Delivery · Plan", hrefs.plan(id));
    if (pl === undefined) return { blocks: [p(s("Still reading Delivery.", plan))], followUps: [] };
    if (!pl) return { blocks: [p(s(`${d.name} has no contract in Oura yet, so there is no plan. It is added under Delivery, Plan.`, plan))], followUps: ["Where is the contract?"] };
    const stage = pl.phase === "not_started" ? `${d.name}'s plan has not started yet` : `${d.name} is in month ${pl.month} of ${pl.termMonths}${pl.monthLabel ? ` (${pl.monthLabel})` : ""}`;
    const sentences = [s(`${stage}: ${pl.doneCount.done} of ${pl.doneCount.total} deliverables done${pl.cameBack ? `, ${n(pl.cameBack, "fix")} came back` : ""}.`, src("Delivery", hrefs.delivery(id)))];
    if (pl.due) sentences.push(s(`The next due date is ${dayMonth(pl.due)}.`, src("Delivery", hrefs.delivery(id))));
    sentences.push(s("What was sold, the term and the key pages are on the Plan view.", plan));
    return { blocks: [p(...sentences), { kind: "go", go: { label: "Open the Plan", to: hrefs.plan(id) } }], followUps: ["What's waiting on us?", "What should I do next?"] };
}

/* ------------------------------------------------------------------------- *
 * The people and the business
 * ------------------------------------------------------------------------- */

function peopleAnswer(d: ClientData): AskAnswer {
    const id = d.businessId;
    const f = d.facts;
    const contact = contactOf(d);
    const client = src("Client tab", hrefs.client(id));
    const sentences = [];
    sentences.push(contact === "the client" ? s(`Oura has no named contact for ${d.name} yet. The contact is set on the Client tab.`, client) : s(`The contact at ${d.name} is ${contact}.`, client));
    if (d.delivery?.accountManager) sentences.push(s(`Account manager: ${d.delivery.accountManager}.`, src("Delivery", hrefs.delivery(id))));
    const about = [f?.category, f?.city, f?.domain].filter((x): x is string => !!x);
    if (about.length) sentences.push(s(`${d.name}: ${about.join(" · ")}.`, src("Overview", hrefs.overview(id))));
    sentences.push(s("Phone numbers and outlets are on the Setup form answers, on the Client tab.", client));
    return { blocks: [p(...sentences)], followUps: [`What's waiting on ${contact}?`, "Catch me up"] };
}

/* ------------------------------------------------------------------------- *
 * Setup and access
 * ------------------------------------------------------------------------- */

function setupAnswer(d: ClientData): AskAnswer {
    const id = d.businessId;
    const f = d.facts;
    const connectors = src("Connectors", `${hrefs.overview(id)}/connectors`);
    if (!f || !f.connectors) return { blocks: [p(s(`Oura has not read ${d.name}'s connections yet.`, connectors))], followUps: ["Catch me up"] };
    // An empty list is no evidence that anything is connected.
    if (f.connectors.length === 0) return { blocks: [p(s(`Oura has no connection status for ${d.name} yet.`, connectors))], followUps: ["Catch me up"] };
    const on = f.connectors.filter((c) => c.status === "connected").map((c) => c.label);
    const off = f.connectors.filter((c) => c.status !== "connected");
    const sentences = [];
    if (on.length) sentences.push(s(`Connected: ${andList(on)}.`, connectors));
    if (off.length) sentences.push(s(`Not connected: ${andList(off.map((c) => (c.status === "action_needed" ? `${c.label} (needs attention)` : c.label)))}.`, connectors));
    else sentences.push(s("Everything Oura can connect is connected.", connectors));
    sentences.push(s("The client's own setup steps (form answers, logins) are on the Onboarding tab.", src("Onboarding", `${hrefs.overview(id)}/onboarding`)));
    return { blocks: [p(...sentences), { kind: "go", go: { label: "Open Connectors", to: `${hrefs.overview(id)}/connectors` } }], followUps: ["How many clicks on Google?", "Catch me up"] };
}

/** The fact answer for a question, or null when it asks for none. */
export function factAnswer(t: string, d: ClientData): AskAnswer | null {
    switch (factOf(t)) {
        case "score":
            return scoreAnswer(d);
        case "dates":
            return datesAnswer(d);
        case "questions":
            return questionsAnswer(t, d);
        case "search":
            return searchAnswer(d);
        case "issues":
            return issuesAnswer(d);
        case "plan":
            return planAnswer(d);
        case "people":
            return peopleAnswer(d);
        case "setup":
            return setupAnswer(d);
        default:
            return null;
    }
}

/** The questions this file answers, for the Did you mean pool and the menu. */
export const FACT_POOL = [
    "What's their score?",
    "When was the last scan?",
    "Which questions are they not named in?",
    "How many clicks on Google?",
    "What's broken on their site?",
    "What's in their plan?",
    "Who is the contact?",
    "What access are we missing?",
];
